/**
 * CRM Contracts List New - Listado de contratos para agentes (new)
 * Ruta: /crm/contratos-new
 * Accesible para admin, lawyer y agent
 * Búsqueda con debounce (300ms) para evitar peticiones en cada tecla.
 */
import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/common/Modal';
import { contractsService } from '@/services/contractsService';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import type { Contract, ContractFilters, ContractStats } from '@/types/contracts';
import {
  CONTRACT_STATUS_COLORS,
  EMPTY_CONTRACT_STATS,
  KYC_STATUS_COLORS,
  GRADE_COLORS,
} from '@/types/contracts';
import { formatDate, formatCurrency } from '@/utils/formatters';
import { formatContractStatus, formatKYCStatus } from '@/utils/statusTranslations';

export function CRMContractsListNew() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ContractStats>(EMPTY_CONTRACT_STATS);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ContractFilters>({
    status: 'all',
    kyc_status: 'all',
    grade: 'all',
    payment_type: 'all',
    sort_by: 'created_at_desc',
    search: '',
    skip: 0,
    limit: 20,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Expirar contrato individual: modal de confirmación + código en proceso
  const [expireTarget, setExpireTarget] = useState<Contract | null>(null);
  const [expiring, setExpiring] = useState(false);
  // Expirar en bloque: contratos pending con fecha pasada
  const [showBulkExpireConfirm, setShowBulkExpireConfirm] = useState(false);
  const [bulkExpiring, setBulkExpiring] = useState(false);
  // Búsqueda: input inmediato + debounce 300ms para no disparar API en cada tecla
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Copy short_url feedback
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    loadContracts();
  }, [filters]);

  // Debounce: actualizar filters.search 300ms después de que el usuario deje de escribir
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();
      setFilters((prev) => {
        if (trimmed === (prev.search || '')) return prev;
        return { ...prev, search: trimmed, skip: 0 };
      });
      debounceRef.current = null;
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const loadContracts = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await contractsService.getContracts(filters);
      setContracts(Array.isArray(response.items) ? response.items : []);
      setTotal(typeof response.total === 'number' ? response.total : 0);
      if (response.stats) setStats(response.stats);
    } catch (error) {
      console.error('Error cargando contratos:', error);
      setLoadError(t('contracts.loadError'));
      setContracts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const handleFilterChange = (key: keyof ContractFilters, value: string | number) => {
    setFilters((prev) => ({ ...prev, [key]: value, skip: 0 }));
  };

  /** Filtrar por estado haciendo clic en una tarjeta de estadística. */
  const handleStatCardClick = (status: string) => {
    const next = status === (filters.status || 'all') ? 'all' : status;
    setFilters((prev) => ({ ...prev, status: next as ContractFilters['status'], skip: 0 }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setFilters({
      status: 'all',
      kyc_status: 'all',
      grade: 'all',
      payment_type: 'all',
      sort_by: 'created_at_desc',
      search: '',
      skip: 0,
      limit: filters.limit,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await contractsService.exportContracts(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contratos-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exportando contratos:', error);
      toast.error(t('contracts.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    const currentSkip = filters.skip ?? 0;
    const currentLimit = filters.limit ?? 20;
    if (direction === 'prev' && currentSkip > 0) {
      setFilters((prev) => ({
        ...prev,
        skip: Math.max(0, (prev.skip ?? 0) - (prev.limit ?? 20)),
      }));
    } else if (direction === 'next' && currentSkip + currentLimit < total) {
      setFilters((prev) => ({
        ...prev,
        skip: (prev.skip ?? 0) + (prev.limit ?? 20),
      }));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, skip: 0 }));
  };

  const handleCopyLink = async (e: React.MouseEvent, contract: Contract) => {
    e.stopPropagation();
    const url = contract.short_url || `${window.location.origin}/contratacion/${contract.hiring_code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(contract.hiring_code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  const handleExpire = async () => {
    if (!expireTarget) return;
    setExpiring(true);
    try {
      await contractsService.expireContract(expireTarget.hiring_code);
      toast.success(t('contracts.expireSuccess', { code: expireTarget.hiring_code }));
      setExpireTarget(null);
      await loadContracts();
    } catch (err: unknown) {
      console.error('Error expirando contrato:', err);
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail =
        typeof e?.response?.data?.detail === 'string'
          ? e.response.data.detail
          : e?.message || t('contracts.expireError');
      toast.error(detail);
    } finally {
      setExpiring(false);
    }
  };

  const handleBulkExpire = async () => {
    setBulkExpiring(true);
    try {
      const result = await contractsService.bulkExpireExpired();
      toast.success(
        result.expired_count > 0
          ? t('contracts.bulkExpireSuccess', { count: result.expired_count })
          : t('contracts.bulkExpireNoContracts')
      );
      setShowBulkExpireConfirm(false);
      await loadContracts();
    } catch (err: unknown) {
      console.error('Error en expiración masiva:', err);
      toast.error(t('contracts.bulkExpireError'));
    } finally {
      setBulkExpiring(false);
    }
  };

  /** Un contrato está "vigente sin firmar" si sigue en pending (no pagado, no firmado, no expirado). */
  const isPendingUnsigned = (contract: Contract) =>
    contract.status === 'pending' && !contract.contract_accepted;

  const currentSkip = filters.skip ?? 0;
  const currentLimit = filters.limit ?? 20;
  const currentPage = Math.floor(currentSkip / currentLimit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / currentLimit));

  /** Número de filtros activos (excluye search que tiene su propio input). */
  const activeFilterCount = [
    filters.status !== 'all' && filters.status,
    filters.grade !== 'all' && filters.grade,
    filters.payment_type !== 'all' && filters.payment_type,
    filters.kyc_status !== 'all' && filters.kyc_status,
    filters.sort_by !== 'created_at_desc' && filters.sort_by,
  ].filter(Boolean).length;

  const hasActiveFilters =
    activeFilterCount > 0 || (filters.search?.trim() ?? '') !== '';

  /** El status filter restringe los items pero stats ignora el status → mostramos total real. */
  const statusIsFiltered = filters.status && filters.status !== 'all';

  /** Para contratos pending: badge de alerta de expiración próxima o vencimiento. */
  const getExpiryWarning = (contract: Contract): { label: string; className: string } | null => {
    if (contract.status !== 'pending' || !contract.expires_at) return null;
    const diffMs = new Date(contract.expires_at).getTime() - Date.now();
    const daysLeft = Math.ceil(diffMs / 86_400_000);
    if (daysLeft <= 0) return { label: t('contracts.expiredLabel'), className: 'bg-red-100 text-red-800' };
    if (daysLeft <= 3) return { label: t('contracts.expiringLabel', { days: daysLeft }), className: 'bg-orange-100 text-orange-800' };
    if (daysLeft <= 7) return { label: t('contracts.expiringLabel', { days: daysLeft }), className: 'bg-amber-100 text-amber-800' };
    return null;
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {t('contracts.title')}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('contracts.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Link to="/crm/contratos-new/plantillas-anexos">
            <Button variant="outline" size="sm" className="w-full sm:w-auto">
              <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
              {t('contracts.templatesButton')}
            </Button>
          </Link>
          <Button
            onClick={() => setShowBulkExpireConfirm(true)}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto text-amber-700 border-amber-300 hover:bg-amber-50"
            title={t('contracts.bulkExpireButton')}
          >
            <ClockIcon className="w-4 h-4 mr-2" />
            {t('contracts.bulkExpireButton')}
          </Button>
          <Button
            onClick={() => navigate('/crm/contratos-new/crear')}
            size="sm"
            className="w-full sm:w-auto"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t('contracts.createButton')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || total === 0}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            {exporting ? t('common.exporting') : t('common.exportCsv')}
          </Button>
        </div>
      </div>

      {/* Estadísticas — tarjetas clicables para filtrar por estado */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4">
        {/* Total — ocupa 2 cols en mobile para centrar el layout de 5 tarjetas */}
        <Card
          onClick={() => handleStatCardClick('all')}
          className={`col-span-2 sm:col-span-1 cursor-pointer transition-all hover:shadow-md ${
            !statusIsFiltered ? 'ring-2 ring-gray-400' : ''
          }`}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <p className="text-xs font-medium text-gray-500">{t('contracts.stats.total')}</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <DocumentTextIcon className="hidden sm:block w-8 h-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        {/* Pendientes */}
        <Card
          onClick={() => handleStatCardClick('pending')}
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.status === 'pending' ? 'ring-2 ring-yellow-400' : ''
          }`}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <p className="text-xs font-medium text-gray-500">{t('contracts.stats.pending')}</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <CalendarIcon className="hidden sm:block w-8 h-8 text-yellow-300" />
            </div>
          </CardContent>
        </Card>
        {/* Pagados */}
        <Card
          onClick={() => handleStatCardClick('paid')}
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.status === 'paid' ? 'ring-2 ring-green-400' : ''
          }`}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <p className="text-xs font-medium text-gray-500">{t('contracts.stats.paid')}</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.paid}</p>
              </div>
              <CurrencyDollarIcon className="hidden sm:block w-8 h-8 text-green-300" />
            </div>
          </CardContent>
        </Card>
        {/* Completados */}
        <Card
          onClick={() => handleStatCardClick('completed')}
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.status === 'completed' ? 'ring-2 ring-blue-400' : ''
          }`}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <p className="text-xs font-medium text-gray-500">{t('contracts.stats.completed')}</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.completed}</p>
              </div>
              <UsersIcon className="hidden sm:block w-8 h-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        {/* Expirados */}
        <Card
          onClick={() => handleStatCardClick('expired')}
          className={`cursor-pointer transition-all hover:shadow-md ${
            filters.status === 'expired' ? 'ring-2 ring-red-400' : ''
          }`}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <p className="text-xs font-medium text-gray-500">{t('contracts.stats.expired')}</p>
                <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <ClockIcon className="hidden sm:block w-8 h-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Búsqueda y Filtros */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <Input
                placeholder={t('contracts.searchPlaceholder')}
                className="pl-9 sm:pl-10 text-sm sm:text-base"
                value={searchInput}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="relative"
              >
                <FunnelIcon className="w-4 h-4 mr-2" />
                {t('common.filters')}
                {activeFilterCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center w-4 h-4 text-xs font-bold bg-white text-gray-800 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-4 h-4 mr-1" />
                  {t('common.clearFilters')}
                </Button>
              )}
            </div>
            {showFilters && (
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                <select
                  value={filters.status || 'all'}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-white"
                >
                  <option value="all">{t('contracts.filters.allStatus')}</option>
                  <option value="pending">{t('contracts.filters.pending')}</option>
                  <option value="paid">{t('contracts.filters.paid')}</option>
                  <option value="completed">{t('contracts.filters.completed')}</option>
                  <option value="expired">{t('contracts.filters.expired')}</option>
                  <option value="cancelled">{t('contracts.filters.cancelled')}</option>
                </select>
                <select
                  value={filters.grade || 'all'}
                  onChange={(e) => handleFilterChange('grade', e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-white"
                >
                  <option value="all">{t('contracts.filters.allGrades')}</option>
                  <option value="A">{t('contracts.filters.gradeA')}</option>
                  <option value="B">{t('contracts.filters.gradeBPlus')}</option>
                  <option value="C">{t('contracts.filters.gradeC')}</option>
                  <option value="T">{t('contracts.filters.gradeT')}</option>
                </select>
                <select
                  value={filters.payment_type || 'all'}
                  onChange={(e) => handleFilterChange('payment_type', e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-white"
                >
                  <option value="all">{t('contracts.filters.allPayments')}</option>
                  <option value="one_time">{t('contracts.filters.oneTimeHalf')}</option>
                  <option value="one_time_full">{t('contracts.filters.oneTimeFull')}</option>
                  <option value="subscription">{t('contracts.filters.subscription')}</option>
                </select>
                <select
                  value={filters.kyc_status || 'all'}
                  onChange={(e) => handleFilterChange('kyc_status', e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-white"
                >
                  <option value="all">{t('contracts.filters.allKYC')}</option>
                  <option value="pending">{t('contracts.filters.kycPending')}</option>
                  <option value="verified">{t('contracts.filters.kycVerified')}</option>
                  <option value="failed">{t('contracts.filters.kycFailed')}</option>
                </select>
                <select
                  value={filters.sort_by || 'created_at_desc'}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-white"
                >
                  <option value="created_at_desc">{t('contracts.filters.newestFirst')}</option>
                  <option value="created_at_asc">{t('contracts.filters.oldestFirst')}</option>
                  <option value="expires_at_asc">{t('contracts.filters.expiringFirst')}</option>
                  <option value="amount_desc">{t('contracts.filters.higherAmount')}</option>
                </select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Contratos */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : loadError ? (
        <QueryErrorState message={loadError} onRetry={loadContracts} />
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="p-12">
            {hasActiveFilters ? (
              <>
                <EmptyState
                  icon={<FunnelIcon className="w-12 h-12" />}
                  title={t('contracts.emptyState.filtered')}
                  description={t('contracts.emptyState.filteredDesc', { total: stats.total })}
                />
                <div className="flex justify-center mt-4">
                  <Button variant="outline" onClick={handleClearFilters}>
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    {t('common.clearFilters')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <EmptyState
                  icon={<DocumentTextIcon className="w-12 h-12" />}
                  title={t('contracts.emptyState.empty')}
                  description={t('contracts.emptyState.emptyDesc')}
                />
                <div className="flex justify-center mt-4">
                  <Button onClick={() => navigate('/crm/contratos-new/crear')}>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {t('contracts.emptyState.createButton')}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3 sm:space-y-4">
            {contracts.map((contract) => (
              <Card
                key={contract.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/crm/contratos-new/${contract.hiring_code}`)}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-base sm:text-lg text-gray-900">
                          {contract.hiring_code}
                        </h3>
                        <Badge
                          className={
                            CONTRACT_STATUS_COLORS[contract.status] || 'bg-gray-100 text-gray-800'
                          }
                        >
                          {formatContractStatus(contract.status)}
                        </Badge>
                        {contract.kyc_status && (
                          <Badge
                            className={
                              KYC_STATUS_COLORS[contract.kyc_status] || 'bg-gray-100 text-gray-800'
                            }
                          >
                            KYC: {formatKYCStatus(contract.kyc_status)}
                          </Badge>
                        )}
                        {contract.grade && (
                          <Badge
                            className={GRADE_COLORS[contract.grade] || 'bg-gray-100 text-gray-800'}
                          >
                            {contract.grade}
                          </Badge>
                        )}
                        {contract.contract_type && contract.contract_type !== 'individual' && (
                          <Badge className="bg-purple-100 text-purple-800 capitalize">
                            {t(`contracts.contractTypes.${contract.contract_type}`, contract.contract_type)}
                          </Badge>
                        )}
                        {(() => {
                          const w = getExpiryWarning(contract);
                          return w ? (
                            <Badge className={w.className} title={t('contracts.expiresLabel', { date: formatDate(contract.expires_at) })}>
                              <ClockIcon className="w-3 h-3 mr-1 inline" />
                              {w.label}
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <UsersIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{contract.client_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DocumentTextIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{contract.service_name}</span>
                        </div>
                        {(contract.created_at || (contract.status === 'pending' && contract.expires_at)) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <CalendarIcon className="w-4 h-4 flex-shrink-0" />
                            {contract.created_at && (
                              <span>{t('contracts.createdLabel', { date: formatDate(contract.created_at) })}</span>
                            )}
                            {contract.status === 'pending' && contract.expires_at && (
                              <>
                                {contract.created_at && <span className="text-gray-400">·</span>}
                                <span className={
                                  (() => {
                                    const diffMs = new Date(contract.expires_at).getTime() - Date.now();
                                    const days = Math.ceil(diffMs / 86_400_000);
                                    return days <= 3 ? 'text-red-500 font-medium' : days <= 7 ? 'text-amber-600 font-medium' : '';
                                  })()
                                }>
                                  {t('contracts.expiresLabel', { date: formatDate(contract.expires_at) })}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2 sm:gap-4 w-full sm:w-auto">
                      <div className="text-right">
                        <p className="text-lg sm:text-xl font-bold text-green-600">
                          {formatCurrency(contract.amount, contract.currency, false)}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/crm/contratos-new/${contract.hiring_code}`);
                          }}
                          className="w-full sm:w-auto"
                        >
                          <EyeIcon className="w-4 h-4 mr-2" />
                          {t('common.viewDetail')}
                        </Button>
                        {(contract.status === 'pending' || contract.status === 'paid') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleCopyLink(e, contract)}
                            className="w-full sm:w-auto text-gray-600"
                            title={t('contracts.copyLinkTitle')}
                          >
                            {copiedCode === contract.hiring_code ? (
                              <CheckCircleIcon className="w-4 h-4 text-green-600" />
                            ) : (
                              <ClipboardDocumentIcon className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        {isPendingUnsigned(contract) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpireTarget(contract);
                            }}
                            className="w-full sm:w-auto text-amber-700 border-amber-300 hover:bg-amber-50"
                            title={t('contracts.expireButton.title')}
                          >
                            <ClockIcon className="w-4 h-4 mr-2" />
                            {t('contracts.expireButton.text')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Paginación: siempre visible cuando hay resultados */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    {t('common.showing', { from: currentSkip + 1, to: Math.min(currentSkip + currentLimit, total), total })}
                    {statusIsFiltered && stats.total !== total && (
                      <span className="text-gray-400"> ({t('common.total')}: {stats.total})</span>
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600">{t('common.perPage')}</span>
                    <select
                      value={currentLimit}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                      className="px-2 py-1.5 border rounded-md text-sm bg-white"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange('prev')}
                      disabled={currentSkip === 0}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      {t('common.previous')}
                    </Button>
                    <span className="text-sm text-gray-600 px-2">
                      {t('common.page', { current: currentPage, total: totalPages })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange('next')}
                      disabled={currentSkip + currentLimit >= total}
                    >
                      {t('common.next')}
                      <ChevronRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Modal confirmar expirar contrato individual */}
      <Modal
        open={expireTarget !== null}
        onClose={() => !expiring && setExpireTarget(null)}
        title={t('contracts.expireModal.title')}
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setExpireTarget(null)}
              disabled={expiring}
              className="text-gray-600 border-gray-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleExpire}
              disabled={expiring}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {expiring ? t('contracts.expireModal.loading') : t('contracts.expireModal.button')}
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          {t('contracts.expireModal.confirmation', { code: expireTarget?.hiring_code, name: expireTarget?.client_name })}
        </p>
        <p className="text-gray-500 text-sm mt-2">
          {t('contracts.expireModal.warning')}
        </p>
      </Modal>

      {/* Modal confirmar expiración masiva */}
      <Modal
        open={showBulkExpireConfirm}
        onClose={() => !bulkExpiring && setShowBulkExpireConfirm(false)}
        title={t('contracts.bulkExpireModal.title')}
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setShowBulkExpireConfirm(false)}
              disabled={bulkExpiring}
              className="text-gray-600 border-gray-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleBulkExpire}
              disabled={bulkExpiring}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {bulkExpiring ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  {t('contracts.bulkExpireModal.loading')}
                </>
              ) : (
                t('contracts.bulkExpireModal.button')
              )}
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          {t('contracts.bulkExpireModal.description')}
        </p>
        <p className="text-gray-500 text-sm mt-2">
          {t('contracts.bulkExpireModal.warning')}
        </p>
      </Modal>
    </div>
  );
}
